import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const animations = {
  fadeInUp: {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  },
  scaleIn: {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1, transition: { duration: 0.25 } },
  },
  slideIn: {
    initial: { x: -100, opacity: 0 },
    animate: { x: 0, opacity: 1, transition: { duration: 0.3 } },
  },
  slideInFromRight: {
    initial: { x: 100, opacity: 0 },
    animate: { x: 0, opacity: 1, transition: { duration: 0.25 } },
  },
  staggerChildren: {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  },
};

const styles = {
  // Typography
  sectionTitle: "font-barlow-semibold text-5xl lg:text-7xl",
  sectionTitleSizes: {
    xs: "text-2xl lg:text-3xl",
    sm: "text-3xl lg:text-4xl",
    md: "text-4xl lg:text-5xl",
    lg: "text-5xl lg:text-6xl",
    xl: "text-5xl lg:text-7xl",
  },

  // Layout
  container: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8",

  // Position
  overlay: "absolute inset-0",

  // Interactive elements
  button:
    "bg-primary hover:bg-primary-dark transition-colors duration-300 ease-in-out",

  // Shadows
  shadowBase: "shadow-md hover:shadow-lg transition-shadow duration-300",
  shadowLarge: "shadow-lg hover:shadow-xl transition-shadow duration-300",

  // Spacing
  sectionSpacing: "py-16 md:py-20 lg:py-24",
  elementSpacing: "space-y-6 md:space-y-8",

  // Transitions
  transitionBase: "transition-all duration-50 ease-in-out",
  transitionSlow: "transition-all duration-100 ease-in-out",
};

const Hero = ({ title, subtitle, backgroundImage }) => (
  <motion.div
    initial="initial"
    animate="animate"
    variants={animations.staggerChildren}
    className="relative h-[80vh] bg-fixed bg-cover bg-center flex items-center justify-center"
    style={{ backgroundImage: `url('${backgroundImage}')` }}
  >
    <div className={`${styles.overlay} bg-black/50`} />
    <div className="relative z-10 text-center mt-16 px-4 md:px-8">
      <motion.h1
        variants={animations.fadeInUp}
        className={`${styles.sectionTitle} text-white text-center mb-6`}
      >
        {title}
      </motion.h1>
      <motion.p
        variants={animations.fadeInUp}
        className="text-xl text-white/90 max-w-3xl mx-auto"
      >
        {subtitle}
      </motion.p>
    </div>
  </motion.div>
);

const SectionHeader = ({
  title,
  subtitle,
  className = "",
  size = "xl",
  titleClassName = "",
}) => (
  <div className={`text-center mb-12 ${className}`}>
    <h2
      className={`font-barlow-semibold ${
        styles.sectionTitleSizes[size] || styles.sectionTitleSizes.xl
      } text-primary mb-6 ${titleClassName}`}
    >
      {title}
    </h2>
    {subtitle && (
      <p className="text-gray-600 max-w-3xl mx-auto text-lg leading-relaxed">
        {subtitle}
      </p>
    )}
  </div>
);

const CTASection = ({ title, subtitle, buttonText, buttonLink }) => (
  <motion.div
    initial="initial"
    whileInView="animate"
    viewport={{ once: true }}
    variants={animations.fadeInUp}
    className="bg-primary py-16 md:py-20 text-center"
  >
    <div className="max-w-3xl mx-auto px-4 md:px-8">
      <h2 className="text-4xl font-bold text-white mb-6">{title}</h2>
      <p className="text-white/90 mb-8 text-lg">{subtitle}</p>
      <Link
        to={buttonLink}
        className={`
          bg-white text-primary px-8 py-3 rounded-lg font-semibold
          hover:bg-gray-50 ${styles.transitionBase}
          ${styles.shadowBase}
        `}
      >
        {buttonText}
      </Link>
    </div>
  </motion.div>
);

const Toast = ({ message, type = "success", onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor =
    type === "success"
      ? "bg-green-500"
      : type === "error"
      ? "bg-red-500"
      : "bg-gray-800";

  return (
    <div
      className={`${bgColor} text-white px-4 py-2 rounded shadow-lg fixed top-4 right-4 z-50`}
    >
      {message}
    </div>
  );
};

export { animations, styles, Hero, SectionHeader, CTASection, Toast };
